import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  Car, QrCode, Clock, Calendar, ChevronRight, LogOut, 
  Check, AlertTriangle, MapPin, FileText, X, Loader2,
  ArrowDownToLine, ArrowUpFromLine, CreditCard, RefreshCw,
  Phone, User, Banknote
} from 'lucide-react';
import Logo from '../components/Logo';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { workerApi } from '../lib/api';
import { logger } from '../lib/logger';
import { fadeInUp, scaleIn, listItem, cardHover, transitions } from '../lib/animations';

// Channel badge component
const ChannelBadge = ({ channel }) => {
  const config = {
    PARKOS: { color: 'bg-blue-500', label: 'Parkos' },
    PARKVIA: { color: 'bg-green-500', label: 'Parkvia' },
    ONEPARK: { color: 'bg-purple-500', label: 'Onepark' },
    LOOKING4PARKING: { color: 'bg-orange-500', label: 'Looking4' },
    FREE2MOVE: { color: 'bg-pink-500', label: 'Free2Move' },
    DIRECT: { color: 'bg-primary', label: 'Directo' },
  };
  const c = config[channel] || config.DIRECT;
  return (
    <Badge className={`${c.color} text-white text-xs`}>
      {c.label}
    </Badge>
  );
};

// Payment status badge
const PaymentBadge = ({ paymentStatus, paymentMethod }) => {
  if (paymentStatus === 'PAID') {
    return (
      <Badge className="bg-green-500/20 text-green-400 text-xs">
        PAGADO
      </Badge>
    );
  }
  if (paymentMethod === 'CASH') {
    return (
      <Badge className="bg-amber-500/20 text-amber-400 text-xs">
        Cobro en parking
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-500/20 text-red-400 text-xs">
      PENDIENTE
    </Badge>
  );
};

// Operational status badge
const StatusBadge = ({ status }) => {
  const config = {
    'PENDING_IN': { color: 'bg-blue-500/20 text-blue-400', label: 'Esperando' },
    'CHECKED_IN': { color: 'bg-green-500/20 text-green-400', label: 'En parking' },
    'CHECKED_OUT': { color: 'bg-purple-500/20 text-purple-400', label: 'Entregado' },
    'COMPLETED': { color: 'bg-zinc-500/20 text-zinc-400', label: 'Completado' },
  };
  const c = config[status] || { color: 'bg-zinc-500/20 text-zinc-400', label: status };
  return (
    <Badge className={`${c.color} text-xs`}>
      {c.label}
    </Badge>
  );
};

// Skeleton loader for reservation cards
const ReservationSkeleton = () => (
  <div className="p-4 border-b border-white/5">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <Skeleton className="h-5 w-24 bg-white/10" />
        <Skeleton className="h-4 w-32 bg-white/10" />
      </div>
      <Skeleton className="h-8 w-20 bg-white/10" />
    </div>
  </div>
);

// Reservation card component
const ReservationCard = ({ 
  reservation, 
  type, 
  onCheckIn, 
  onCheckOut, 
  onMarkPaid,
  actionLoading 
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'es' ? es : enUS;
  
  const timeField = type === 'departures' ? 'end_dt' : 'start_dt';
  const time = reservation[timeField] 
    ? format(new Date(reservation[timeField]), 'HH:mm', { locale })
    : '--:--';
  
  const canCheckIn = reservation.operational_status === 'PENDING_IN' && reservation.status === 'CONFIRMED';
  const canCheckOut = reservation.operational_status === 'CHECKED_IN';
  const canMarkPaid = reservation.payment_status === 'PENDING' && reservation.payment_method === 'CASH';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 border-b border-white/5 hover:bg-white/5 transition-colors"
      data-testid={`reservation-${reservation.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-lg font-bold text-white">{time}</span>
            <StatusBadge status={reservation.operational_status} />
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-primary font-bold text-lg">
              {reservation.vehicle_plate || 'Sin matrícula'}
            </span>
            <ChannelBadge channel={reservation.source_channel || 'DIRECT'} />
          </div>
          
          <div className="flex items-center gap-4 text-sm text-zinc-400">
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              <span className="truncate max-w-[120px]">{reservation.customer_name}</span>
            </div>
            {reservation.customer_phone && (
              <a 
                href={`tel:${reservation.customer_phone}`}
                className="flex items-center gap-1 text-primary hover:underline"
              >
                <Phone className="w-3 h-3" />
                <span>{reservation.customer_phone}</span>
              </a>
            )}
          </div>
          
          <div className="flex items-center gap-2 mt-2">
            <PaymentBadge 
              paymentStatus={reservation.payment_status} 
              paymentMethod={reservation.payment_method} 
            />
            <span className="text-sm text-zinc-500">
              {(reservation.total_cents / 100).toFixed(2)}€
            </span>
            <span className="text-xs text-zinc-600">
              {reservation.reservation_code}
            </span>
          </div>
        </div>
        
        {/* Right: Actions */}
        <div className="flex flex-col gap-2">
          {canCheckIn && (
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => onCheckIn(reservation.id)}
              disabled={actionLoading === reservation.id}
              data-testid={`checkin-${reservation.id}`}
            >
              {actionLoading === reservation.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <ArrowDownToLine className="w-4 h-4 mr-1" />
                  Check-in
                </>
              )}
            </Button>
          )}
          
          {canCheckOut && (
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => onCheckOut(reservation.id)}
              disabled={actionLoading === reservation.id}
              data-testid={`checkout-${reservation.id}`}
            >
              {actionLoading === reservation.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <ArrowUpFromLine className="w-4 h-4 mr-1" />
                  Check-out
                </>
              )}
            </Button>
          )}
          
          {canMarkPaid && (
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => onMarkPaid(reservation.id)}
              disabled={actionLoading === reservation.id}
              data-testid={`markpaid-${reservation.id}`}
            >
              {actionLoading === reservation.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Banknote className="w-4 h-4 mr-1" />
                  Cobrado
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const WorkerDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isWorker } = useAuth();
  const [activeTab, setActiveTab] = useState('arrivals');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const locale = i18n.language === 'es' ? es : enUS;

  // Redirect if not worker
  useEffect(() => {
    if (!isWorker) {
      navigate('/worker/login');
    }
  }, [isWorker, navigate]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await workerApi.getDashboard();
      setDashboardData(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      logger.error('Error fetching dashboard:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (isWorker) {
      fetchDashboard();
    }
  }, [isWorker, fetchDashboard]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (isWorker && !actionLoading) {
        fetchDashboard(true);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isWorker, actionLoading, fetchDashboard]);

  // Action handlers with optimistic UI
  const handleCheckIn = async (reservationId) => {
    setActionLoading(reservationId);
    try {
      const response = await workerApi.quickCheckIn(reservationId);
      toast.success(response.data.message || 'Check-in realizado');
      fetchDashboard(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al hacer check-in');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCheckOut = async (reservationId) => {
    setActionLoading(reservationId);
    try {
      const response = await workerApi.quickCheckOut(reservationId);
      toast.success(response.data.message || 'Check-out realizado');
      fetchDashboard(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al hacer check-out');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkPaid = async (reservationId) => {
    setActionLoading(reservationId);
    try {
      const response = await workerApi.markPaid(reservationId);
      toast.success(response.data.message || 'Pago registrado');
      fetchDashboard(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setActionLoading(null);
    }
  };

  // QR Scanner handlers
  const handleScan = async (result) => {
    if (!result || !result[0]) return;
    
    const token = result[0].rawValue;
    setShowScanner(false);
    
    try {
      const response = await workerApi.scanQR(token);
      setScanResult(response.data);
      
      if (response.data.ok) {
        toast.success(response.data.message);
        fetchDashboard(true);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error al procesar QR');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/worker/login');
  };

  // Memoized stats
  const stats = useMemo(() => dashboardData?.stats || {
    arrivals_count: 0,
    departures_count: 0,
    unpaid_count: 0,
    arrivals_pending_checkin: 0,
    departures_pending_checkout: 0
  }, [dashboardData]);

  if (!isWorker) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-heavy sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Logo className="h-10" />
              <div>
                <span className="font-heading font-bold text-white">WORKER</span>
                <p className="text-xs text-zinc-400">{user?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchDashboard(true)}
                disabled={refreshing}
                className="text-zinc-400"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              
              <Button
                onClick={() => setShowScanner(true)}
                className="bg-primary text-black hover:bg-primary/90"
                data-testid="scan-qr-btn"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Escanear QR
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-red-400 hover:bg-red-400/10 flex items-center gap-2 px-3"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline text-sm">Salir</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 py-4">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <motion.div
            className={`card-dark p-3 cursor-pointer transition-all ${activeTab === 'arrivals' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setActiveTab('arrivals')}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-2 mb-1">
              <ArrowDownToLine className="w-4 h-4 text-green-400" />
              <span className="text-xs text-zinc-400">Llegadas</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-white">{stats.arrivals_count}</span>
              {stats.arrivals_pending_checkin > 0 && (
                <span className="text-xs text-amber-400">
                  ({stats.arrivals_pending_checkin} pendientes)
                </span>
              )}
            </div>
          </motion.div>

          <motion.div
            className={`card-dark p-3 cursor-pointer transition-all ${activeTab === 'departures' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setActiveTab('departures')}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpFromLine className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-zinc-400">Salidas</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-white">{stats.departures_count}</span>
              {stats.departures_pending_checkout > 0 && (
                <span className="text-xs text-amber-400">
                  ({stats.departures_pending_checkout} pendientes)
                </span>
              )}
            </div>
          </motion.div>

          <motion.div
            className={`card-dark p-3 cursor-pointer transition-all ${activeTab === 'unpaid' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setActiveTab('unpaid')}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-zinc-400">Por cobrar</span>
            </div>
            <span className="text-2xl font-bold text-white">{stats.unpaid_count}</span>
          </motion.div>
        </div>

        {/* Last refresh indicator */}
        {lastRefresh && (
          <p className="text-xs text-zinc-600 text-right mb-2">
            Actualizado: {format(lastRefresh, 'HH:mm:ss', { locale })}
          </p>
        )}

        {/* Tabs Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-secondary mb-4">
            <TabsTrigger value="arrivals" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              Llegadas
            </TabsTrigger>
            <TabsTrigger value="departures" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              Salidas
            </TabsTrigger>
            <TabsTrigger value="unpaid" className="data-[state=active]:bg-primary data-[state=active]:text-black">
              Por cobrar
            </TabsTrigger>
          </TabsList>

          {/* Arrivals Tab */}
          <TabsContent value="arrivals" className="mt-0">
            <div className="card-dark overflow-hidden">
              {loading ? (
                <>
                  <ReservationSkeleton />
                  <ReservationSkeleton />
                  <ReservationSkeleton />
                </>
              ) : dashboardData?.arrivals?.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <ArrowDownToLine className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay llegadas para hoy</p>
                </div>
              ) : (
                <AnimatePresence>
                  {dashboardData?.arrivals?.map((r) => (
                    <ReservationCard
                      key={r.id}
                      reservation={r}
                      type="arrivals"
                      onCheckIn={handleCheckIn}
                      onCheckOut={handleCheckOut}
                      onMarkPaid={handleMarkPaid}
                      actionLoading={actionLoading}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </TabsContent>

          {/* Departures Tab */}
          <TabsContent value="departures" className="mt-0">
            <div className="card-dark overflow-hidden">
              {loading ? (
                <>
                  <ReservationSkeleton />
                  <ReservationSkeleton />
                  <ReservationSkeleton />
                </>
              ) : dashboardData?.departures?.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <ArrowUpFromLine className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay salidas para hoy</p>
                </div>
              ) : (
                <AnimatePresence>
                  {dashboardData?.departures?.map((r) => (
                    <ReservationCard
                      key={r.id}
                      reservation={r}
                      type="departures"
                      onCheckIn={handleCheckIn}
                      onCheckOut={handleCheckOut}
                      onMarkPaid={handleMarkPaid}
                      actionLoading={actionLoading}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </TabsContent>

          {/* Unpaid Tab */}
          <TabsContent value="unpaid" className="mt-0">
            <div className="card-dark overflow-hidden">
              {loading ? (
                <>
                  <ReservationSkeleton />
                  <ReservationSkeleton />
                  <ReservationSkeleton />
                </>
              ) : dashboardData?.unpaid?.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">
                  <Banknote className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay pagos pendientes</p>
                </div>
              ) : (
                <AnimatePresence>
                  {dashboardData?.unpaid?.map((r) => (
                    <ReservationCard
                      key={r.id}
                      reservation={r}
                      type="unpaid"
                      onCheckIn={handleCheckIn}
                      onCheckOut={handleCheckOut}
                      onMarkPaid={handleMarkPaid}
                      actionLoading={actionLoading}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* QR Scanner Dialog */}
      <Dialog open={showScanner} onOpenChange={setShowScanner}>
        <DialogContent className="bg-background border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary" />
              Escanear código QR
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative aspect-square bg-black rounded-lg overflow-hidden">
            <Scanner
              onScan={handleScan}
              onError={(error) => logger.error('Scanner error:', error)}
              styles={{
                container: { width: '100%', height: '100%' },
                video: { width: '100%', height: '100%', objectFit: 'cover' }
              }}
            />
          </div>
          
          <p className="text-sm text-zinc-400 text-center">
            Apunta la cámara al código QR del cliente
          </p>
        </DialogContent>
      </Dialog>

      {/* Scan Result Dialog */}
      <Dialog open={!!scanResult} onOpenChange={() => setScanResult(null)}>
        <DialogContent className="bg-background border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {scanResult?.ok ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              )}
              {scanResult?.ok ? 'Operación exitosa' : 'Error'}
            </DialogTitle>
          </DialogHeader>
          
          <p className={scanResult?.ok ? 'text-green-400' : 'text-red-400'}>
            {scanResult?.message}
          </p>
          
          {scanResult?.reservation && (
            <div className="mt-4 p-4 bg-secondary rounded-lg">
              <p className="text-sm text-zinc-400">Reserva</p>
              <p className="text-lg font-mono text-primary">
                {scanResult.reservation.reservation_code}
              </p>
              <p className="text-white">{scanResult.reservation.vehicle_plate}</p>
            </div>
          )}
          
          <Button onClick={() => setScanResult(null)} className="mt-4">
            Cerrar
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerDashboard;
